import React from "react";
import { useCurrency } from "../context/CurrencyContext";
import { useRBAC } from "../hooks/useRBAC";
import { FX_RATES } from "../constants";

// =============================================================================
// CurrencyToggle — per-user USD ⇄ <their currency> display switch (sidebar footer)
// =============================================================================
// Renders nothing unless the user has a supported non-USD display_currency
// (Rayston client → SGD, Nathaniel + Mako → AUD). Flips EVERY dollar on screen
// between USD and that currency. The "$" symbol is unchanged in both — this
// labeled pill is the ONLY signal of which currency the "$" means, so the active
// segment is highlighted and the code is spelled out.
// (Mako 2026-07-15: "just show the $ amount as long as the button can tell you.")
// =============================================================================

export const CurrencyToggle: React.FC<{ collapsed?: boolean }> = ({ collapsed }) => {
  const { canToggleCurrency } = useRBAC();
  const { code, target, options, setMode, toggle } = useCurrency();

  if (!canToggleCurrency || !target) return null;

  const meta = FX_RATES[target]; // rate provenance for the user's currency

  // Collapsed rail (w-14): single compact button showing the active code.
  if (collapsed) {
    const other = code === "USD" ? target : "USD";
    return (
      <div className="px-2 py-2 border-t border-black/[0.08] flex-shrink-0 flex justify-center">
        <button
          onClick={toggle}
          className="token-10 font-semibold text-glass px-2 py-1 rounded-md bg-black/[0.04] border border-black/[0.08] hover:bg-black/[0.06] anim-tap transition-colors"
          title={`Display currency: ${code} — click to switch to ${other}`}
          aria-label={`Display currency ${code}, click to switch`}
        >
          {code}
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 border-t border-black/[0.08] flex-shrink-0">
      <div className="flex items-center justify-between gap-2">
        <span className="token-10 uppercase text-glass-muted tracking-wider">Currency</span>
        {/* Segmented pill — active segment is filled so it's unmistakable which
            currency every "$" is showing. Right-aligned per Mako's "bottom right".
            Options are [USD, <user's currency>] — driven by display_currency. */}
        <div
          className="inline-flex items-center rounded-lg bg-black/[0.04] border border-black/[0.08] p-0.5"
          role="group"
          aria-label="Display currency"
          data-testid="currency-toggle"
        >
          {options.map((opt) => {
            const active = code === opt;
            return (
              <button
                key={opt}
                onClick={() => {
                  if (!active) setMode(opt);
                }}
                aria-pressed={active}
                className={`token-10 font-semibold px-2 py-0.5 rounded-md anim-tap transition-colors ${
                  active ? "bg-blue-500 text-white shadow-sm" : "text-glass-muted hover:text-glass"
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
      {/* Rate provenance — no "$" symbol here (it's rate metadata, not a money
          value), so it stays honest without violating the plain-$ rule. */}
      <p className="token-9 text-glass-muted mt-1 text-right tabular-nums">
        {meta ? `1 USD = ${meta.rate.toFixed(2)} ${target} · as of ${meta.asOf}` : "rate loading…"}
      </p>
    </div>
  );
};

export default CurrencyToggle;
